import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Pencil, Save, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function CurrencyMasterPage() {
    const { hasPerm } = useAuth();
    const canWrite = hasPerm("currencies", "write");
    const [rows, setRows] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ code: "", name: "", units_per_usd: "", symbol: "" });
    const [adding, setAdding] = useState(false);

    const load = async () => {
        const { data } = await api.get("/currencies");
        setRows(data);
    };
    useEffect(() => {
        load();
    }, []);

    const startEdit = (r) => {
        setEditing(r.code);
        // Display as "1 USD = X units of this currency" (inverse of stored rate_to_usd)
        const usdPerUnit = r.rate_to_usd && Number(r.rate_to_usd) !== 0 ? 1 / Number(r.rate_to_usd) : 0;
        setForm({ code: r.code, name: r.name, units_per_usd: usdPerUnit, symbol: r.symbol });
    };

    const save = async () => {
        try {
            const unitsPerUsd = parseFloat(form.units_per_usd);
            if (!unitsPerUsd || unitsPerUsd <= 0) {
                toast.error("Rate must be greater than zero");
                return;
            }
            // Convert "1 USD = X units" back to rate_to_usd (USD per 1 unit) for storage
            const rate_to_usd = 1 / unitsPerUsd;
            await api.post("/currencies", {
                code: form.code,
                name: form.name,
                rate_to_usd,
                symbol: form.symbol,
            });
            toast.success(`Saved ${form.code}`);
            setEditing(null);
            setAdding(false);
            setForm({ code: "", name: "", units_per_usd: "", symbol: "" });
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (code) => {
        if (!window.confirm(`Delete currency ${code}?`)) return;
        await api.delete(`/currencies/${code}`);
        await load();
    };

    return (
        <div data-testid="currencies-page">
            <PageHeader
                title="Currency Master"
                eyebrow="Masters"
                description="Foreign exchange rates against USD. Voice base = USD, SMS base = EUR. Manual override allowed on transactions."
                action={
                    canWrite && !adding && !editing ? (
                        <Button
                            className="rounded-sm bg-[#002FA7] hover:bg-[#001f6f]"
                            onClick={() => {
                                setAdding(true);
                                setForm({ code: "", name: "", units_per_usd: "", symbol: "" });
                            }}
                            data-testid="currency-add-btn"
                        >
                            + New currency
                        </Button>
                    ) : null
                }
            />

            <div className="bg-white border border-slate-200 overflow-hidden">
                <table className="w-full text-[13px] financial" data-testid="currencies-table">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                            <th className="px-4 py-2.5 w-24">Code</th>
                            <th className="px-4 py-2.5">Name</th>
                            <th className="px-4 py-2.5 num">1 USD = (units)</th>
                            <th className="px-4 py-2.5 w-24">Symbol</th>
                            <th className="px-4 py-2.5 w-40 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {adding && (
                            <EditRow form={form} setForm={setForm} onSave={save} onCancel={() => setAdding(false)} isNew />
                        )}
                        {rows.map((r) =>
                            editing === r.code ? (
                                <EditRow key={r.code} form={form} setForm={setForm} onSave={save} onCancel={() => setEditing(null)} />
                            ) : (
                                <tr key={r.code} className="border-b border-slate-100" data-testid={`currency-row-${r.code}`}>
                                    <td className="px-4 py-2 font-mono font-semibold">{r.code}</td>
                                    <td className="px-4 py-2">{r.name}</td>
                                    <td className="px-4 py-2 num">
                                        {r.rate_to_usd && Number(r.rate_to_usd) !== 0
                                            ? (1 / Number(r.rate_to_usd)).toFixed(4)
                                            : "—"}{" "}
                                        <span className="text-slate-400 font-mono text-[11px]">{r.code}</span>
                                    </td>
                                    <td className="px-4 py-2 font-mono">{r.symbol}</td>
                                    <td className="px-4 py-2 text-right">
                                        {canWrite && (
                                            <>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => startEdit(r)}
                                                    data-testid={`currency-edit-${r.code}`}
                                                    className="h-7 px-2"
                                                >
                                                    <Pencil size={13} />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => remove(r.code)}
                                                    data-testid={`currency-delete-${r.code}`}
                                                    className="h-7 px-2 text-red-600"
                                                >
                                                    <X size={13} />
                                                </Button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ),
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function EditRow({ form, setForm, onSave, onCancel, isNew }) {
    return (
        <tr className="border-b border-slate-100 bg-blue-50/30">
            <td className="px-4 py-2">
                <Input
                    value={form.code}
                    disabled={!isNew}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    className="h-8 rounded-sm font-mono"
                    data-testid="currency-input-code"
                />
            </td>
            <td className="px-4 py-2">
                <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="h-8 rounded-sm"
                    data-testid="currency-input-name"
                />
            </td>
            <td className="px-4 py-2">
                <div className="flex items-center gap-1.5">
                    <span className="text-[11px] font-mono text-slate-500">1 USD =</span>
                    <Input
                        value={form.units_per_usd}
                        type="number"
                        step="0.000001"
                        onChange={(e) => setForm({ ...form, units_per_usd: e.target.value })}
                        className="h-8 rounded-sm font-mono text-right"
                        data-testid="currency-input-rate"
                    />
                    <span className="text-[11px] font-mono text-slate-500">{form.code || "units"}</span>
                </div>
            </td>
            <td className="px-4 py-2">
                <Input
                    value={form.symbol}
                    onChange={(e) => setForm({ ...form, symbol: e.target.value })}
                    className="h-8 rounded-sm"
                    data-testid="currency-input-symbol"
                />
            </td>
            <td className="px-4 py-2 text-right">
                <Button
                    size="sm"
                    onClick={onSave}
                    className="h-7 px-2 rounded-sm bg-[#002FA7]"
                    data-testid="currency-save-btn"
                >
                    <Save size={12} className="mr-1" /> Save
                </Button>
                <Button size="sm" variant="ghost" onClick={onCancel} className="h-7 px-2 ml-1">
                    Cancel
                </Button>
            </td>
        </tr>
    );
}

export function PageHeader({ title, description, eyebrow, action }) {
    return (
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-4 sm:mb-6 gap-3 sm:gap-6">
            <div className="min-w-0">
                {eyebrow && <div className="text-[10px] uppercase tracking-widest text-slate-500">{eyebrow}</div>}
                <h1 className="text-xl sm:text-3xl font-semibold tracking-tight truncate">{title}</h1>
                {description && <p className="text-slate-500 text-[12.5px] sm:text-[13px] mt-1 sm:mt-1.5 max-w-2xl">{description}</p>}
            </div>
            {action && <div className="flex-shrink-0">{action}</div>}
        </div>
    );
}
